import { Head, Link, router } from "@inertiajs/react"
import { Trash2, Check, X, ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type DeletionRequestRow = {
  id: number
  requestable_type: string
  requestable_id: number
  requestable_name: string
  requestable_slug: string | null
  requester_name: string
  reason: string | null
  created_at: string
}

const ENTITY_PATH: Record<string, string> = {
  Band: "bands",
  Person: "people",
  Recording: "recordings",
  Venue: "venues",
  RecordLabel: "record_labels",
}

function entityUrl(row: DeletionRequestRow): string | null {
  const base = ENTITY_PATH[row.requestable_type]
  if (!base) return null
  return `/${base}/${row.requestable_slug ?? row.requestable_id}`
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
}

function ApproveButton({ id }: { id: number }) {
  return (
    <button
      type="button"
      onClick={() => {
        if (confirm("Approve this deletion request? This will permanently delete the record.")) {
          router.patch(`/admin/deletion_requests/${id}/approve`)
        }
      }}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors"
    >
      <Check className="w-3 h-3" /> Approve & Delete
    </button>
  )
}

function RejectButton({ id }: { id: number }) {
  return (
    <button
      type="button"
      onClick={() => {
        const notes = prompt("Reason for rejection (optional):")
        if (notes === null) return
        router.patch(`/admin/deletion_requests/${id}/reject`, { notes })
      }}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-stone-600 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
    >
      <X className="w-3 h-3" /> Reject
    </button>
  )
}

export default function DeletionRequestsIndex({ requests }: { requests: DeletionRequestRow[] }) {
  return (
    <>
      <Head title="Deletion Requests">
        <meta name="description" content="Review pending deletion requests." />
        <meta property="og:title" content="Deletion Requests" />
        <meta property="og:description" content="Review pending deletion requests." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex items-center gap-3">
            <Trash2 className="h-5 w-5 text-ink-muted" />
            <div>
              <h1>Deletion Requests</h1>
              <p className="mt-1">
                {requests.length === 0
                  ? "No pending deletion requests."
                  : `${requests.length} pending ${requests.length === 1 ? "request" : "requests"} awaiting review.`}
              </p>
            </div>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="mt-12 flex flex-col items-center gap-3 text-ink-muted">
            <Check className="h-8 w-8" />
            <p className="text-sm">All clear — nothing pending.</p>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {requests.map((req) => {
              const url = entityUrl(req)
              return (
                <li key={req.id} className="px-4 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="neutral">{req.requestable_type}</Badge>
                        <span className="font-medium text-ink-display">{req.requestable_name}</span>
                        {url && (
                          <Link
                            href={url}
                            className="inline-flex items-center gap-0.5 text-xs text-accent no-underline hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" /> View
                          </Link>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-ink-muted">
                        Requested by <span className="font-medium">{req.requester_name}</span> on {formatDate(req.created_at)}
                      </div>
                      {req.reason && (
                        <div className="mt-1.5 rounded-md bg-stone-50 dark:bg-stone-900 px-3 py-2 text-xs text-ink-body">
                          &ldquo;{req.reason}&rdquo;
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <ApproveButton id={req.id} />
                      <RejectButton id={req.id} />
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </AdminShell>
    </>
  )
}
